import type { SleepEvent, SleepGoal } from './types';

export interface AICoachTemporalContext {
  currentTime: string; // ISO string
  timezone: string; // IANA timezone
  currentTimeFormatted: string; // e.g. "Tuesday, 13 Oct 2026 at 20:36 (8:36 PM)"
  currentStatusSummary: string;
  formattedSleepData: string;
}

/**
 * Safely extracts a native JavaScript Date from any event timestamp representation.
 * Supports Firestore Timestamp (.toDate()), Date objects, ISO strings, epoch milliseconds,
 * and serialized Firestore timestamps ({ _seconds, _nanoseconds } or { seconds, nanoseconds }).
 */
export function extractDateFromEvent(event: any): Date | null {
  if (!event || !event.time) return null;
  try {
    const t = event.time;
    if (t instanceof Date) {
      return isNaN(t.getTime()) ? null : t;
    }
    if (typeof t.toDate === 'function') {
      const d = t.toDate();
      return isNaN(d.getTime()) ? null : d;
    }
    if (typeof t === 'string' || typeof t === 'number') {
      const d = new Date(t);
      return isNaN(d.getTime()) ? null : d;
    }
    if (typeof t === 'object') {
      if (t._seconds !== undefined) {
        const ms = t._seconds * 1000 + (t._nanoseconds || 0) / 1000000;
        const d = new Date(ms);
        return isNaN(d.getTime()) ? null : d;
      }
      if (t.seconds !== undefined) {
        const ms = t.seconds * 1000 + (t.nanoseconds || 0) / 1000000;
        const d = new Date(ms);
        return isNaN(d.getTime()) ? null : d;
      }
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Formats a duration in minutes into a human-readable string (e.g. "1h 45m" or "35m").
 */
export function formatMinutesDuration(totalMinutes: number): string {
  const rounded = Math.round(Math.max(0, totalMinutes));
  const hours = Math.floor(rounded / 60);
  const mins = rounded % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

/**
 * Formats a time in both 24-hour and 12-hour format: e.g. "14:30 (2:30 PM)"
 */
export function formatTime24and12(date: Date, timezone?: string): string {
  const tz = timezone || 'UTC';
  try {
    const time24 = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);

    const time12 = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);

    return `${time24} (${time12})`;
  } catch {
    return date.toISOString().slice(11, 16);
  }
}

/**
 * Formats a calendar date key for grouping: YYYY-MM-DD in the target timezone.
 */
export function getCalendarDateKey(date: Date, timezone?: string): string {
  const tz = timezone || 'UTC';
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
    return parts; // en-CA formats as YYYY-MM-DD
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

/**
 * Formats the header for a day group (e.g. "Today - Tuesday, 13 Oct 2026").
 */
export function formatDayHeader(date: Date, now: Date, timezone?: string): string {
  const tz = timezone || 'UTC';
  const eventKey = getCalendarDateKey(date, tz);
  const nowKey = getCalendarDateKey(now, tz);

  const yesterdayDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const yesterdayKey = getCalendarDateKey(yesterdayDate, tz);

  let prefix = '';
  if (eventKey === nowKey) {
    prefix = 'Today - ';
  } else if (eventKey === yesterdayKey) {
    prefix = 'Yesterday - ';
  }

  try {
    const formatted = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
    return `${prefix}${formatted} (${eventKey})`;
  } catch {
    return `${prefix}${eventKey}`;
  }
}

/**
 * Returns human-readable relative/local time for chat history messages.
 * e.g. "Today at 2:30 PM (14:30)" or "Yesterday at 9:15 AM (09:15)"
 */
export function formatMessageTimeForAI(date: Date, now: Date, timezone?: string): string {
  const tz = timezone || 'UTC';
  const msgKey = getCalendarDateKey(date, tz);
  const nowKey = getCalendarDateKey(now, tz);

  const yesterdayDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const yesterdayKey = getCalendarDateKey(yesterdayDate, tz);

  const timeStr = formatTime24and12(date, tz);

  if (msgKey === nowKey) {
    return `Today at ${timeStr}`;
  }
  if (msgKey === yesterdayKey) {
    return `Yesterday at ${timeStr}`;
  }

  try {
    const dateStr = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      month: 'short',
      day: 'numeric',
    }).format(date);
    return `${dateStr} at ${timeStr}`;
  } catch {
    return `${msgKey} at ${timeStr}`;
  }
}

/**
 * Returns the period of the day based on local hour.
 */
export function getTimeOfDay(date: Date = new Date(), timezone?: string): 'morning' | 'afternoon' | 'evening' | 'night' {
  const tz = timezone || 'UTC';
  let hour = 12;
  try {
    const hourStr = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      hour12: false,
    }).format(date);
    hour = parseInt(hourStr, 10);
  } catch {
    hour = date.getUTCHours();
  }

  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
}

interface ValidEvent {
  event: SleepEvent;
  date: Date;
}

function getSortedValidEvents(events: SleepEvent[]): ValidEvent[] {
  if (!events || !Array.isArray(events)) return [];

  const valid: ValidEvent[] = [];
  for (const event of events) {
    const date = extractDateFromEvent(event);
    if (date) {
      valid.push({ event, date });
    }
  }

  // Sort chronologically (oldest to newest)
  valid.sort((a, b) => a.date.getTime() - b.date.getTime());
  return valid;
}

/**
 * Formats sleep events into an unambiguous, structured, chronological timeline for the AI.
 */
export function formatEventsForAICoach(
  events: SleepEvent[],
  now: Date = new Date(),
  options: { timezone?: string; maxDays?: number } = {}
): string {
  const tz = options.timezone || 'UTC';
  const maxDays = options.maxDays ?? 7;

  const valid = getSortedValidEvents(events);
  if (valid.length === 0) {
    return 'No sleep data available.';
  }

  // Filter events within maxDays
  const cutoffTime = now.getTime() - maxDays * 24 * 60 * 60 * 1000;
  const recent = valid.filter(v => v.date.getTime() >= cutoffTime);

  if (recent.length === 0) {
    return `No sleep data recorded in the last ${maxDays} days.`;
  }

  // Group events by day key
  const dayGroups: Map<string, ValidEvent[]> = new Map();
  for (const item of recent) {
    const dayKey = getCalendarDateKey(item.date, tz);
    if (!dayGroups.has(dayKey)) {
      dayGroups.set(dayKey, []);
    }
    dayGroups.get(dayKey)!.push(item);
  }

  const lines: string[] = [];

  for (const dayEvents of dayGroups.values()) {
    const firstDate = dayEvents[0].date;
    const header = formatDayHeader(firstDate, now, tz);
    lines.push(`\n[${header}]`);

    for (const item of dayEvents) {
      const timeStr = formatTime24and12(item.date, tz);
      const e = item.event;
      let desc = '';

      switch (e.type) {
        case 'wake':
          desc = 'Morning / Night Sleep Wake-up';
          break;
        case 'bedtime':
          desc = 'Bedtime (Night Sleep)';
          break;
        case 'nap_start':
          desc = 'Nap Started';
          break;
        case 'nap_end':
          desc = 'Nap Ended';
          break;
        case 'nap':
          desc = `Nap (Daytime Sleep)${e.duration ? ` - duration: ${formatMinutesDuration(e.duration)}` : ''}`;
          break;
        case 'night_wake':
          desc = `Night Waking${e.duration ? ` - duration: ${formatMinutesDuration(e.duration)}` : ''}`;
          break;
        case 'feed':
          desc = 'Feeding';
          break;
        default:
          desc = e.type;
      }

      if (e.notes && e.notes.trim()) {
        desc += ` [Notes: ${e.notes.trim()}]`;
      }

      lines.push(`  - ${timeStr}: ${desc}`);
    }
  }

  return lines.join('\n').trim();
}

/**
 * Computes a rich, real-time status summary of the child relative to the current moment.
 * Eliminates LLM arithmetic errors for current awake windows, active naps, and bedtime countdowns.
 */
export function computeCurrentStatusSummary(
  events: SleepEvent[],
  now: Date = new Date(),
  goal?: SleepGoal,
  timezone?: string
): string {
  const tz = timezone || 'UTC';
  const valid = getSortedValidEvents(events);

  const statusLines: string[] = [];

  // Filter events up to 'now'
  const pastEvents = valid.filter(v => v.date.getTime() <= now.getTime());

  if (pastEvents.length === 0) {
    return 'No sleep events logged yet. Current time is established, but no prior sleep activity is on record today.';
  }

  const latest = pastEvents[pastEvents.length - 1];
  const elapsedMs = now.getTime() - latest.date.getTime();
  const elapsedMins = Math.max(0, Math.floor(elapsedMs / 60000));
  const elapsedFormatted = formatMinutesDuration(elapsedMins);
  const latestTimeStr = formatTime24and12(latest.date, tz);

  // 1. Current State
  if (latest.event.type === 'wake' || latest.event.type === 'nap_end') {
    statusLines.push(`- **Current State**: AWAKE`);
    statusLines.push(`  - Last woke at: ${latestTimeStr} (${elapsedFormatted} ago).`);
    statusLines.push(`  - Child has been awake for: ${elapsedFormatted}.`);

    if (goal && goal.maxAwakeWindow) {
      const maxAwakeMins = goal.maxAwakeWindow * 60;
      const targetNextSleepDate = new Date(latest.date.getTime() + maxAwakeMins * 60000);
      const targetTimeStr = formatTime24and12(targetNextSleepDate, tz);

      if (elapsedMins < maxAwakeMins) {
        const remainingMins = maxAwakeMins - elapsedMins;
        statusLines.push(`  - Target Max Awake Window: ${goal.maxAwakeWindow} hours (${formatMinutesDuration(maxAwakeMins)}).`);
        statusLines.push(`  - Remaining Awake Time: ${formatMinutesDuration(remainingMins)}.`);
        statusLines.push(`  - Target next sleep (nap/bedtime): around ${targetTimeStr}.`);
      } else {
        const excessMins = elapsedMins - maxAwakeMins;
        statusLines.push(`  - **ALERT**: Target awake window is ${goal.maxAwakeWindow}h, but child has been awake for ${elapsedFormatted} (EXCEEDED by ${formatMinutesDuration(excessMins)}). The child is overdue for sleep and may be overtired.`);
      }
    }
  } else if (latest.event.type === 'nap_start') {
    statusLines.push(`- **Current State**: ASLEEP (Daytime Nap)`);
    statusLines.push(`  - Fell asleep at: ${latestTimeStr} (napping for ${elapsedFormatted} so far).`);
  } else if (latest.event.type === 'bedtime') {
    statusLines.push(`- **Current State**: ASLEEP (Night Sleep)`);
    statusLines.push(`  - Went to bed at: ${latestTimeStr} (asleep for ${elapsedFormatted} so far).`);
  } else if (latest.event.type === 'nap') {
    // Single nap event
    const durationMins = latest.event.duration || 60;
    const napEndTime = new Date(latest.date.getTime() + durationMins * 60000);
    if (now.getTime() < napEndTime.getTime()) {
      const remainingNapMins = Math.floor((napEndTime.getTime() - now.getTime()) / 60000);
      statusLines.push(`- **Current State**: ASLEEP (Daytime Nap)`);
      statusLines.push(`  - Started at: ${latestTimeStr}. Scheduled duration: ${formatMinutesDuration(durationMins)} (${formatMinutesDuration(remainingNapMins)} remaining).`);
    } else {
      const awakeMins = Math.floor((now.getTime() - napEndTime.getTime()) / 60000);
      statusLines.push(`- **Current State**: AWAKE`);
      statusLines.push(`  - Nap ended at: ${formatTime24and12(napEndTime, tz)} (${formatMinutesDuration(awakeMins)} ago).`);
      statusLines.push(`  - Child has been awake for: ${formatMinutesDuration(awakeMins)}.`);
    }
  } else {
    // Feed or night_wake
    statusLines.push(`- **Latest Event Logged**: ${latest.event.type} at ${latestTimeStr} (${elapsedFormatted} ago).`);
  }

  // 2. Today's Activity
  const nowKey = getCalendarDateKey(now, tz);
  const todaysEvents = pastEvents.filter(v => getCalendarDateKey(v.date, tz) === nowKey);

  if (todaysEvents.length > 0) {
    const morningWake = todaysEvents.find(v => v.event.type === 'wake');
    const naps = todaysEvents.filter(v => v.event.type === 'nap' || v.event.type === 'nap_end');

    statusLines.push(`- **Today's Logged Summary**:`);
    if (morningWake) {
      statusLines.push(`  - Morning Wake: ${formatTime24and12(morningWake.date, tz)}.`);
    }
    statusLines.push(`  - Naps Logged Today: ${naps.length}.`);
  } else {
    statusLines.push(`- **Today's Logs**: No events logged yet for today (${nowKey}). Last logged event was on a prior day.`);
  }

  // 3. Bedtime Goal Comparison
  if (goal && goal.bedtime) {
    try {
      // Parse goal.bedtime (HH:mm) for today
      const [bHourStr, bMinStr] = goal.bedtime.split(':');
      const bHour = parseInt(bHourStr, 10);
      const bMin = parseInt(bMinStr, 10);

      if (!isNaN(bHour) && !isNaN(bMin)) {
        // Construct bedtime date object in user's timezone
        const nowParts = new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: false,
        }).formatToParts(now);

        const currentHour = parseInt(nowParts.find(p => p.type === 'hour')?.value || '0', 10);
        const currentMin = parseInt(nowParts.find(p => p.type === 'minute')?.value || '0', 10);

        const nowMinutesSinceMidnight = currentHour * 60 + currentMin;
        const bedtimeMinutesSinceMidnight = bHour * 60 + bMin;
        const diffMins = bedtimeMinutesSinceMidnight - nowMinutesSinceMidnight;

        if (diffMins > 0) {
          statusLines.push(`- **Target Bedtime Tonight**: ${goal.bedtime} (in ${formatMinutesDuration(diffMins)}).`);
        } else {
          statusLines.push(`- **Target Bedtime Tonight**: ${goal.bedtime} (${formatMinutesDuration(Math.abs(diffMins))} ago - current time is past bedtime goal).`);
        }
      }
    } catch {
      // Ignore parsing errors for goal bedtime
    }
  }

  return statusLines.join('\n');
}

/**
 * Creates the complete temporal context payload for the AI coach.
 */
export function getAICoachTemporalContext(
  events: SleepEvent[],
  now: Date = new Date(),
  goal?: SleepGoal,
  userTimezone?: string
): AICoachTemporalContext {
  const timezone = userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const currentTime = now.toISOString();

  let currentTimeFormatted = '';
  try {
    const fullFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    const time24 = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(now);

    currentTimeFormatted = `${fullFormatter.format(now)} (${time24})`;
  } catch {
    currentTimeFormatted = currentTime;
  }

  const currentStatusSummary = computeCurrentStatusSummary(events, now, goal, timezone);
  const formattedSleepData = formatEventsForAICoach(events, now, { timezone });

  return {
    currentTime,
    timezone,
    currentTimeFormatted,
    currentStatusSummary,
    formattedSleepData,
  };
}
